import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Progress, Row, Select, Space, Statistic, Tag, Typography, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined, ReloadOutlined, SoundOutlined, StopOutlined } from '@ant-design/icons';
import ToolPageHeader from '../../components/ToolPageHeader';
import { useTranslation } from '../../hooks/useTranslation';
import {
  estimateMp3Bytes, MAX_MP3_BYTES, MP3_BITRATES, Mp3CompressionError, Mp3CompressionJob,
  Mp3Metadata, Mp3Stage, validateMp3File,
} from '../../helpers/mp3Compression';
import './index.css';

const { Text, Title } = Typography;
const formatSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;
const formatDuration = (seconds: number) => {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 3600).toString().padStart(2, '0')}:${Math.floor(total / 60 % 60).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
};
type Status = 'idle' | Mp3Stage | 'completed' | 'error';
interface Result { url: string; size: number; name: string }

const Mp3Compressor: React.FC = () => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`mp3Compressor.${key}`);
  const [file, setFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [metadata, setMetadata] = useState<Mp3Metadata | null>(null);
  const [previewDuration, setPreviewDuration] = useState(0);
  const [bitrate, setBitrate] = useState(128);
  const [status, setStatus] = useState<Status>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const jobRef = useRef<Mp3CompressionJob | null>(null);
  const sourceUrlRef = useRef('');
  const resultUrlRef = useRef('');
  const busy = ['loading', 'analyzing', 'compressing', 'finalizing'].includes(status);
  const duration = metadata?.duration || previewDuration;
  const estimatedBytes = duration ? estimateMp3Bytes(duration, bitrate) : 0;
  const outputTooLarge = estimatedBytes > MAX_MP3_BYTES;

  useEffect(() => () => {
    jobRef.current?.cancel();
    jobRef.current = null;
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
  }, []);

  const clearResult = () => {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
    resultUrlRef.current = '';
    setResult(null);
  };

  const selectFile = (nextFile: File) => {
    if (jobRef.current) return;
    try {
      validateMp3File(nextFile);
    } catch (e) {
      setError(e instanceof Mp3CompressionError ? e.code : 'invalidFile');
      return;
    }
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(nextFile);
    sourceUrlRef.current = url;
    setSourceUrl(url);
    setFile(nextFile);
    setMetadata(null);
    setPreviewDuration(0);
    setStatus('idle');
    setError('');
    setNotice('');
    setProgress(0);
    clearResult();
  };

  const compress = async () => {
    if (!file || jobRef.current || outputTooLarge) return;
    const job = new Mp3CompressionJob();
    jobRef.current = job;
    setStatus('loading');
    setError('');
    setNotice('');
    setProgress(0);
    clearResult();
    try {
      const output = await job.run(file, bitrate, (update) => {
        if (jobRef.current !== job) return;
        setStatus(update.stage);
        setProgress(update.progress);
        if (update.metadata) setMetadata(update.metadata);
      });
      if (jobRef.current !== job) return;
      const url = URL.createObjectURL(output.blob);
      resultUrlRef.current = url;
      setResult({ url, size: output.blob.size, name: `${file.name.replace(/\.mp3$/i, '')}-compressed.mp3` });
      setProgress(100);
      setStatus('completed');
    } catch (e) {
      if (jobRef.current !== job) return;
      setError(e instanceof Mp3CompressionError ? e.code : 'compressionFailed');
      setStatus('error');
    } finally {
      if (jobRef.current === job) jobRef.current = null;
    }
  };

  const cancel = () => {
    const job = jobRef.current;
    jobRef.current = null;
    job?.cancel();
    setStatus('idle');
    setProgress(0);
    setNotice('cancelled');
  };

  const preset = [64, 128, 192].includes(bitrate) ? bitrate : 'custom';
  const uploadProps = {
    accept: '.mp3,audio/mpeg', multiple: false, showUploadList: false, disabled: busy,
    beforeUpload: (nextFile: File) => { selectFile(nextFile); return false; },
  };

  return (
    <div className="mp3-compressor-page">
      <Card className="mp3-compressor-card">
        <ToolPageHeader backTo="/image-processing" backText={tr('back')}
          title={tr('title')} subtitle={tr('subtitle')} icon={<SoundOutlined aria-hidden="true" />}
          extra={<Tag color="blue">{tr('localBadge')}</Tag>} />
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={13}>
            <Card className="mp3-panel">
              <div className="mp3-panel-heading">
                <Title level={3}>{tr('sourceTitle')}</Title>
                {file && <Upload {...uploadProps}><Button icon={<ReloadOutlined aria-hidden="true" />} disabled={busy}>{tr('changeFile')}</Button></Upload>}
              </div>
              {!file ? <Upload.Dragger {...uploadProps} className="mp3-dropzone">
                <p className="ant-upload-drag-icon"><InboxOutlined aria-hidden="true" /></p>
                <p className="ant-upload-text">{tr('uploadHint')}</p>
                <p className="ant-upload-hint">{tr('uploadLimit')}</p>
              </Upload.Dragger> : <div className="mp3-source">
                <Text strong className="mp3-filename">{file.name}</Text>
                <Space wrap><Text type="secondary">{formatSize(file.size)}</Text>
                  {duration > 0 && <Tag>{formatDuration(duration)}</Tag>}
                  {metadata && <Tag>{metadata.sampleRate} Hz / {metadata.channels === 1 ? tr('mono') : tr('stereo')}</Tag>}
                </Space>
                <audio key={sourceUrl} controls preload="metadata" src={sourceUrl} aria-label={tr('sourcePreview')}
                  onLoadedMetadata={(event) => {
                    const seconds = event.currentTarget.duration;
                    if (Number.isFinite(seconds) && seconds > 0) setPreviewDuration(seconds);
                  }} />
              </div>}
              <ParagraphNote text={tr('privacy')} />
              <ParagraphNote text={tr('largeFileHint')} />
            </Card>
          </Col>
          <Col xs={24} lg={11}>
            <Card className="mp3-panel">
              <Title level={3}>{tr('settings')}</Title>
              <div className="mp3-setting">
                <label htmlFor="mp3-preset">{tr('quality')}</label>
                <Select id="mp3-preset" value={preset} disabled={busy} onChange={(value) => {
                  if (value !== 'custom') setBitrate(Number(value));
                }} options={[
                  { value: 64, label: tr('small') }, { value: 128, label: tr('balanced') },
                  { value: 192, label: tr('clear') }, { value: 'custom', label: tr('custom'), disabled: true },
                ]} />
              </div>
              <div className="mp3-setting">
                <label htmlFor="mp3-bitrate">{tr('bitrate')}</label>
                <Select id="mp3-bitrate" value={bitrate} disabled={busy} onChange={setBitrate}
                  options={MP3_BITRATES.map((value) => ({ value, label: `${value} kbps` }))} />
              </div>
              <Text type="secondary">{tr('estimated')}: {estimatedBytes ? formatSize(estimatedBytes) : tr('afterAnalysis')}</Text>
              {outputTooLarge && <Alert type="warning" showIcon message={tr('outputTooLarge')} />}
              {!outputTooLarge && file && estimatedBytes >= file.size && <Alert type="warning" showIcon message={tr('mayNotShrink')} />}
              <div className="mp3-actions">
                <Button type="primary" icon={<SoundOutlined aria-hidden="true" />} disabled={!file || busy || outputTooLarge} onClick={compress}>
                  {tr(status === 'error' ? 'retry' : 'start')}
                </Button>
                {busy && <Button icon={<StopOutlined aria-hidden="true" />} onClick={cancel}>{tr('cancel')}</Button>}
              </div>
              {busy && <div role="status" aria-live="polite">
                <Text>{tr(status)}</Text><Progress percent={progress} status="active" />
              </div>}
              {notice && <Alert type="info" showIcon message={tr(notice)} />}
              {error && <Alert type="error" showIcon message={tr(error)} />}
            </Card>
          </Col>
        </Row>
        {result && file && <Card className="mp3-panel mp3-result">
          <Title level={3}>{tr('completed')}</Title>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={8}><Statistic title={tr('originalSize')} value={formatSize(file.size)} /></Col>
            <Col xs={24} sm={8}><Statistic title={tr('resultSize')} value={formatSize(result.size)} /></Col>
            <Col xs={24} sm={8}><Statistic title={tr('saved')} value={(1 - result.size / file.size) * 100} precision={1} suffix="%" /></Col>
          </Row>
          {result.size >= file.size && <Alert type="warning" showIcon message={tr('notSmaller')} />}
          <audio controls preload="metadata" src={result.url} aria-label={tr('resultPreview')} />
          <Button type="primary" href={result.url} download={result.name} icon={<DownloadOutlined aria-hidden="true" />}>{tr('download')}</Button>
        </Card>}
      </Card>
    </div>
  );
};

const ParagraphNote: React.FC<{ text: string }> = ({ text }) => <Typography.Paragraph type="secondary" className="mp3-note">{text}</Typography.Paragraph>;

export default Mp3Compressor;
